/**
 * Grid Primitive - FluentUI Design Kit
 * Responsive grid layout with built-in breakpoint support
 */

import React, { useInsertionEffect, useMemo } from 'react';
import { useFluentTokens } from '../Provider';
import { buildGridStyles, buildResponsiveStyles } from './helpers/gridHelper';

import type { GridProps } from './Grid.types';

/**
 * Grid - A responsive grid layout primitive with Design Kit tokens
 *
 * Automatically handles responsive breakpoints without CSS modules.
 * Supports simple column counts or complex responsive configurations.
 *
 * @example
 * // Simple: 2 columns, auto-responsive
 * <Grid columns={2} gap="l">
 *   <Widget1 />
 *   <Widget2 />
 * </Grid>
 *
 * @example
 * // Advanced: Different columns per breakpoint
 * <Grid
 *   columns={{
 *     mobile: 1,
 *     tablet: 2,
 *     desktop: 3
 *   }}
 *   gap="l"
 * >
 *   <Widget1 />
 *   <Widget2 />
 *   <Widget3 />
 * </Grid>
 *
 * @example
 * // Custom: Use repeat() or fr units
 * <Grid columns="repeat(auto-fit, minmax(300px, 1fr))" gap="m">
 *   {widgets.map(w => <w.component key={w.id} />)}
 * </Grid>
 */
export const Grid = React.forwardRef<HTMLElement, GridProps>(
  (
    {
      children,
      as = 'div',
      columns = 1,
      rows,
      gap,
      rowGap,
      columnGap,
      alignItems = 'start',
      justifyContent,
      justifyItems,
      alignContent,
      width,
      height,
      minHeight,
      maxHeight,
      style = {},
      className,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const { tokens: designKitTokens } = useFluentTokens();

    // Handle responsive columns
    const isResponsive = typeof columns === 'object' && !Array.isArray(columns);
    const responsiveConfig = isResponsive ? columns : null;

    // Build base grid styles
    const gridStyles = buildGridStyles({
      columns,
      rows,
      gap,
      rowGap,
      columnGap,
      alignItems,
      justifyContent,
      justifyItems,
      alignContent,
      width,
      height,
      minHeight,
      maxHeight,
      style,
      isResponsive,
      tokens: designKitTokens,
    });

    // Generate responsive CSS if needed
    const responsiveStyles = isResponsive && responsiveConfig ? buildResponsiveStyles(responsiveConfig) : '';

    // Stable unique class name for responsive styles (created once per mount)
    const uniqueClass = useMemo(
      () => (isResponsive ? `grid-${Math.random().toString(36).substring(7)}` : ''),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    // Inject responsive styles into <head> before paint — avoids render-blocking inline <style> tags
    useInsertionEffect(() => {
      if (!responsiveStyles || !uniqueClass) return;

      const styleEl = document.createElement('style');
      styleEl.dataset.grid = uniqueClass;
      styleEl.textContent = `.${uniqueClass} { ${responsiveStyles} }`;
      document?.head?.appendChild(styleEl);

      return () => styleEl.remove();
    }, [responsiveStyles, uniqueClass]);

    const finalClassName = [className, uniqueClass].filter(Boolean).join(' ');

    return (
      <>
        {React.createElement(
          as,
          {
            ref,
            className: finalClassName || undefined,
            style: gridStyles,
            onClick,
            ...rest,
          },
          children,
        )}
      </>
    );
  },
);

// Add display name for better debugging
Grid.displayName = 'Grid';
